import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../../../../../../config";

const MARKET_CAP_HISTORY_DAYS = 30;

const formatDateParam = (date) => date.toISOString();

const getMarketCapDateRange = () => {
  const timeEnd = new Date();
  const timeStart = new Date();

  timeStart.setDate(timeStart.getDate() - MARKET_CAP_HISTORY_DAYS);

  return {
    time_start: formatDateParam(timeStart),
    time_end: formatDateParam(timeEnd),
  };
};

const formatMarketCapValue = (value) => {
  const number = Number(value) || 0;

  if (!number) {
    return "--";
  }

  return `$${(number / 1_000_000_000_000).toFixed(2)}T`;
};

const formatMarketCapChange = (value) => {
  if (value === null) {
    return "--";
  }

  return `${Number(value) >= 0 ? "+" : ""}${value}%`;
};

export const useMarketCapHistory = () => {
  const [marketCapQuotes, setMarketCapQuotes] = useState([]);
  const [isMarketCapLoading, setIsMarketCapLoading] = useState(true);

  useEffect(() => {
    const fetchMarketCapHistory = async () => {
      const { time_start, time_end } = getMarketCapDateRange();

      try {
        setIsMarketCapLoading(true);

        const response = await axios.get(
          `${config.api_url}/coinmarketcap/global-metrics/history`,
          {
            params: {
              time_start,
              time_end,
              interval: "daily",
              convert: "USD",
            },
          },
        );

        setMarketCapQuotes(response.data?.data?.quotes || []);
      } catch (error) {
        console.error("Failed to fetch market cap history", error);
        setMarketCapQuotes([]);
      } finally {
        setIsMarketCapLoading(false);
      }
    };

    fetchMarketCapHistory();
  }, []);

  const marketCapData = useMemo(() => {
    const marketCapChartData = marketCapQuotes
      .map((marketCapQuote) =>
        Number(marketCapQuote.quote?.USD?.total_market_cap || 0),
      )
      .filter((value) => Number.isFinite(value) && value > 0);

    const previousMarketCap =
      marketCapChartData[marketCapChartData.length - 2] || 0;

    const latestMarketCap =
      marketCapChartData[marketCapChartData.length - 1] || 0;

    const marketCapChange =
      previousMarketCap && latestMarketCap
        ? (
            ((latestMarketCap - previousMarketCap) / previousMarketCap) *
            100
          ).toFixed(2)
        : null;

    return {
      marketCapChartData,
      latestMarketCap,
      formattedMarketCap: formatMarketCapValue(latestMarketCap),
      marketCapChange,
      formattedMarketCapChange: formatMarketCapChange(marketCapChange),
      isMarketCapPositive:
        marketCapChange !== null ? Number(marketCapChange) >= 0 : undefined,
    };
  }, [marketCapQuotes]);

  return {
    ...marketCapData,
    isMarketCapLoading,
  };
};